import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Form, Input, InputNumber, Spin, Table, Typography, message } from 'antd';
import api from '../../services/api';
import { useGlobalContext } from '../../store/GlobalContext';
import { amount } from './format';

const { Title, Paragraph } = Typography;

/** P-12 no-debt affidavit: review known claims and record who confirmed them. */
export default function NoDebtAffidavitPage() {
  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';
  const [noDebt, setNoDebt] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/summary/${estateId}/no-debt`);
      setNoDebt(data); setError('');
    } catch { setError('Unable to load the no-debt review'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (estateId) { setLoading(true); void load(); } }, [estateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (values: any) => {
    setSaving(true);
    try {
      await api.post(`/summary/${estateId}/no-debt/confirm`, values);
      message.success('Confirmation recorded with the financial figures reviewed'); await load();
    } catch (e: any) { message.error(e.response?.data?.message || 'Could not record confirmation'); }
    finally { setSaving(false); }
  };

  if (!estateId) return null;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>No-Debt Affidavit (P-12)</Title>
      <Paragraph type="secondary">Review every known claim against the estate, then record who confirmed the review.</Paragraph>
      {loading ? <Spin /> : <>
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        <Card title="Claims review" extra={<span>Personal property plus projected gross rents: <strong>{amount(noDebt?.total)}</strong></span>}>
          <Table size="small" rowKey="code" pagination={false} dataSource={noDebt?.claims || []} columns={[
            { title: 'Claim', dataIndex: 'name' }, { title: 'Category', dataIndex: 'category' }, { title: 'Outstanding', dataIndex: 'amount', render: amount }, { title: 'Nature', dataIndex: 'description' },
          ]} />
        </Card>
        <Card title="Confirmation" style={{ marginTop: 16 }}>
          {noDebt?.confirmation && <Alert style={{ marginBottom: 16 }} type={noDebt.confirmation.current ? 'info' : 'warning'} message={`${noDebt.confirmation.signerName} confirmed on ${new Date(noDebt.confirmation.createdAt).toLocaleString()}. ${noDebt.confirmation.current ? 'Figures match the confirmation.' : 'Figures have changed; review and confirm again.'} Comparison: ${noDebt.confirmation.withinThreshold ? 'within' : 'above'} the entered limit of ${amount(noDebt.confirmation.threshold)}.`} />}
          <Form layout="vertical" onFinish={confirm} initialValues={{ note: '' }} style={{ maxWidth: 640 }}>
            <Form.Item name="threshold" label="Amount to compare against" rules={[{ required: true }]}><InputNumber min={0} precision={2} /></Form.Item>
            <Form.Item name="signerName" label="Person confirming" rules={[{ required: true, whitespace: true }]}><Input maxLength={255} /></Form.Item>
            <Form.Item name="note" label="Review note / basis for comparison amount"><Input.TextArea maxLength={4000} /></Form.Item>
            <Form.Item name="diligentSearchConfirmed" valuePropName="checked" rules={[{ validator: (_, v) => v === true ? Promise.resolve() : Promise.reject(new Error('Confirmation is required')) }]}><Checkbox>I have made a diligent search and reviewed all known claims, including unpaid funeral and medical bills listed above.</Checkbox></Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>Record confirmation</Button>
          </Form>
        </Card>
      </>}
    </div>
  );
}
